import useListFetch, { UseListFetchProps } from '../../common/useListFetch';
import CmonUsersService from '../../services/cmon/CmonUsersService';
import CcUser from '../../services/models/CcUser';
import { useCallback, useEffect, useReducer, useState } from 'react';
import {
    initialListState,
    listReducer,
    setListAll,
} from '../../common/listReducer';
import { arrayFilter, arrayPages } from '../../common/filtering';
import { getSortAlphabeticFn } from '../../common/sorting';
import { FetchRefreshFunctionParams } from '../../common/useFetch';
import { AppConfig } from '../../AppConfig';
export type UseUsersListProps = Omit<UseListFetchProps, 'fetchFn'> & {
    pageSize?: number;
    useCache?: boolean;
    filters?: Function[];
};

// @todo refactor this to useListFetch file
type ListParams = {
    page?: number;
    pageSize?: number;
    order?: (a: any, b: any) => any;
    filters?: Function[];
};

// @todo refactor this to useListFetch file
type FilterFunctionParams = ListParams & { arr?: any[] };
type RefreshFunctionParams = FetchRefreshFunctionParams;

export default function useUsersList({
    pageSize = AppConfig.defaultPaginationConfig.pageSize,
    useCache,
    filters,
    name,
}: UseUsersListProps) {
    const {
        list: usersList,
        loading,
        loaded,
        refresh: refreshUsers,
        cancel,
    } = useListFetch({
        name: name || 'users-list',
        useCache,
        fetchFn: async ({ ...rest }, opts) => {
            const { users, total } = await CmonUsersService.getUsers(
                {
                    ...rest,
                },
                opts
            );
            return {
                list: users,
                total,
            };
        },
        cancelFn: async ({ requestId }) => {
            await CmonUsersService.cancelRequest(requestId);
        },
    });
    const [list, setList] = useState<CcUser[]>();
    const [total, setTotal] = useState<number>(0);

    const [
        {
            page: listPage,
            pageSize: listPageSize,
            order: listOrder,
            filters: listFilters,
        },
        listDispatch,
    ] = useReducer(listReducer, {
        ...initialListState,
        pageSize: pageSize || 0,
        filters: filters,
        order: getSortAlphabeticFn('ascend', (x) => x.userName),
    });

    const filter = useCallback<(p?: FilterFunctionParams) => void>(
        ({
            page = listPage,
            pageSize = listPageSize,
            order = listOrder,
            filters = listFilters,
            arr = usersList || [],
        } = {}) => {
            listDispatch(setListAll({ page, pageSize, order, filters }));
            const filteredArr = arrayFilter({
                filters: [...(filters || [])],
                arr,
            });

            setList([
                ...arrayPages({
                    page,
                    pageSize,
                    order,
                    arr: filteredArr,
                }),
            ]);
            setTotal(filteredArr.length);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [listPage, listPageSize, listOrder, listFilters, usersList]
    );
    const refresh = useCallback<(p?: RefreshFunctionParams) => Promise<void>>(
        async ({
            page = listPage,
            pageSize = listPageSize,
            order = listOrder,
            filters = listFilters,
            ...rest
        } = {}) => {
            listDispatch(setListAll({ page, pageSize, order, filters }));
            await refreshUsers({
                ...rest,
            });
        },
        []
    );

    useEffect(() => {
        if (usersList) {
            filter({
                arr: usersList,
            });
        }
    }, [usersList]);

    return {
        list,
        loading,
        loaded,
        refresh,
        filter,
        page: listPage,
        pageSize: listPageSize,
        total,
        cancel,
    };
}
